import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import BottomNav from '@/components/BottomNav';
import { UserContext, API } from '@/App';

const PROGRAM_TITLES = {
  friends_10: 'Refer 10 Friends',
  friends_30: 'Refer 30 Friends',
  restaurants_5: 'Refer 5 Restaurants',
  delivery_partners_5: 'Refer 5 Delivery Partners'
};

const PROGRAM_DESCRIPTIONS = {
  friends_10: 'Unlock 1% by referring 10 friends. After their first successful order, you will receive 1% profit of FoodMatrix for the whole month.',
  friends_30: 'Unlock another 1% by referring 30 friends. After their first successful order, you will receive 1% profit of FoodMatrix for the whole month.',
  restaurants_5: 'Unlock 1% by referring 5 restaurants. After their successful registration, you will receive 1% profit of FoodMatrix for the whole month.',
  delivery_partners_5: 'Unlock 1% by referring 5 delivery partners. After their successful registration, you will receive 1% profit of FoodMatrix for the whole month.'
};

const PartnerProgramDetailsPage = () => {
  const navigate = useNavigate();
  const { programType } = useParams();
  const { user } = useContext(UserContext);
  
  // Contact details state
  const [contactDetails, setContactDetails] = useState({
    mobile_number: '',
    whatsapp_number: '',
    email: ''
  });
  const [sameAsMobile, setSameAsMobile] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  
  const programTitle = PROGRAM_TITLES[programType] || 'Partner Program';
  const programDescription = PROGRAM_DESCRIPTIONS[programType] || '';
  
  // Fetch existing contact details on load
  useEffect(() => {
    const fetchContactDetails = async () => {
      try {
        const response = await axios.get(`${API}/referral/contact-details?program_type=${programType}`, { withCredentials: true });
        if (response.data && response.data.mobile_number) {
          setContactDetails({
            mobile_number: response.data.mobile_number || '',
            whatsapp_number: response.data.whatsapp_number || '',
            email: response.data.email || ''
          });
          // Check if whatsapp same as mobile
          if (response.data.mobile_number && response.data.mobile_number === response.data.whatsapp_number) {
            setSameAsMobile(true);
          }
        }
      } catch (err) {
        // No existing data or not logged in
      }
    };
    fetchContactDetails();
  }, [programType]);
  
  const handleMobileChange = (value) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
    setContactDetails(prev => ({
      ...prev,
      mobile_number: digitsOnly,
      whatsapp_number: sameAsMobile ? digitsOnly : prev.whatsapp_number
    }));
    setErrors(prev => ({ ...prev, mobile_number: '' }));
  };
  
  const handleWhatsappChange = (value) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
    setContactDetails(prev => ({
      ...prev,
      whatsapp_number: digitsOnly
    }));
  };
  
  const handleSameAsMobileChange = (checked) => {
    setSameAsMobile(checked);
    if (checked) {
      setContactDetails(prev => ({
        ...prev,
        whatsapp_number: prev.mobile_number
      }));
    }
  };
  
  const validateEmail = (email) => {
    if (!email) return true; // Optional field
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  
  const handleSaveDetails = async () => {
    // Check if user is logged in
    if (!user) {
      toast.error('Please login to save your details');
      navigate('/login');
      return;
    }
    
    // Validate
    const newErrors = {};
    
    if (!contactDetails.mobile_number) {
      newErrors.mobile_number = 'Mobile number is required';
    } else if (contactDetails.mobile_number.length !== 10) {
      newErrors.mobile_number = 'Mobile number must be 10 digits';
    }
    
    if (contactDetails.email && !validateEmail(contactDetails.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setErrors({});
    setSaving(true);
    
    try {
      // Save contact details
      await axios.post(`${API}/referral/contact-details`, {
        ...contactDetails,
        program_type: programType
      }, { withCredentials: true });
      
      // Also register for profit sharing program
      await axios.post(`${API}/profit-sharing/register`, {}, { withCredentials: true });
      
      toast.success('Partner program registration saved successfully!');
      navigate('/profit-sharing');
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Please login to save your details');
        navigate('/login');
      } else {
        toast.error('Failed to save details. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/profit-sharing')} className="p-2 hover:bg-white/20 rounded-full" data-testid="back-btn">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold">{programTitle}</h1>
              <p className="text-amber-100 text-sm">1% Profit Sharing – Partner Program</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Program Description */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-amber-100 rounded-full">
              <TrendingUp className="w-6 h-6 text-amber-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-800">Program Details</h2>
          </div>
          <p className="text-gray-600">{programDescription}</p>
        </div>

        {/* Contact Details Form */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-gray-800">Contact Details</h2>
            <p className="text-sm text-gray-500 mt-1">Provide your contact details to join this partner program.</p>
          </div>
          
          {/* Desktop: 3 columns, Mobile: stacked */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Mobile Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <Input
                type="tel"
                placeholder="Enter 10-digit number"
                value={contactDetails.mobile_number}
                onChange={(e) => handleMobileChange(e.target.value)}
                maxLength={10}
                className={errors.mobile_number ? 'border-red-500' : ''}
                data-testid="partner-mobile"
              />
              {errors.mobile_number && (
                <p className="text-red-500 text-xs mt-1">{errors.mobile_number}</p>
              )}
            </div>
            
            {/* WhatsApp Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                WhatsApp Number
              </label>
              <Input
                type="tel"
                placeholder="Enter 10-digit number"
                value={contactDetails.whatsapp_number}
                onChange={(e) => handleWhatsappChange(e.target.value)}
                maxLength={10}
                disabled={sameAsMobile}
                className={sameAsMobile ? 'bg-gray-100' : ''}
                data-testid="partner-whatsapp"
              />
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  id="same-as-mobile"
                  checked={sameAsMobile}
                  onChange={(e) => handleSameAsMobileChange(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <label htmlFor="same-as-mobile" className="text-xs text-gray-600">
                  Same as Mobile Number
                </label>
              </div>
            </div>
            
            {/* Email ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email ID <span className="text-gray-400 text-xs">(Optional)</span>
              </label>
              <Input
                type="email"
                placeholder="Enter email address"
                value={contactDetails.email}
                onChange={(e) => {
                  setContactDetails(prev => ({ ...prev, email: e.target.value }));
                  setErrors(prev => ({ ...prev, email: '' }));
                }}
                className={errors.email ? 'border-red-500' : ''}
                data-testid="partner-email"
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>
          </div>
          
          <Button
            onClick={handleSaveDetails}
            disabled={saving}
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-6 py-3 rounded-xl font-semibold"
            data-testid="save-details-btn"
          >
            {saving ? 'Saving...' : 'Save Details'}
          </Button>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default PartnerProgramDetailsPage;
